import { useCallback } from 'react'
import { fetchCosts, saveCosts } from '../adminApi'
import { useServerDraft } from '../../hooks/useServerDraft'
import { useLatestLoad } from '../../hooks/useLatestLoad'
import type { CostModel, CostsResponse } from '../../core/types/margin'

/**
 * Eén gedeelde aanvraag voor alle margeblokken op een scherm (de productlijst heeft er tientallen).
 * Een minuut geldig; opslaan in Instellingen of een fout maakt hem meteen leeg.
 */
const TTL_MS = 60_000
let shared: { at: number; promise: Promise<CostsResponse> } | null = null

const loadShared = (): Promise<CostsResponse> => {
  if (!shared || Date.now() - shared.at > TTL_MS) {
    const promise = fetchCosts().catch((err: unknown) => {
      shared = null
      throw err
    })
    shared = { at: Date.now(), promise }
  }
  return shared.promise
}

const toDraft = (r: CostsResponse): CostModel => r.costs
const persist = async (costs: CostModel, r: CostsResponse) => {
  const saved = await saveCosts({ revision: r.revision, costs })
  shared = { at: Date.now(), promise: Promise.resolve(saved) }
  return saved
}

/** Instellingen › Kosten en marge: laden, lokaal aanpassen, in één keer opslaan (PUT /admin/costs). */
export function useCostsDraft() {
  return useServerDraft({ load: fetchCosts, toDraft, persist, savedTitle: 'Kosten opgeslagen', savedText: 'De marges rekenen meteen met de nieuwe getallen.', conflictSubject: 'De kosten' })
}

/**
 * Alleen lezen: kostenmodel en inkoopprijzen voor een margeblok. Zonder `enabled` (geen
 * `finance.margin`) vraagt het niets op: de server zou toch weigeren.
 */
export function useCosts(enabled = true) {
  const load = useCallback(() => (enabled ? loadShared() : Promise.resolve(null)), [enabled])
  const { data, status, reload } = useLatestLoad(load)
  return { data: data ?? null, status, reload }
}
